import { applePayValidateMerchant } from '@/api/logined'

import { getClientNextActionField } from "@/services/clientNextAction";

export default {
    /**
     * 初始化 Fortis 支付客户端
     * @param {Object} params - 初始化参数
     * @returns {Object|null} Google Pay 客户端或 null（Apple Pay）
     */
    async initPayment ({ selectedMethod }) {
        if (selectedMethod === 'web_google_pay') {
            await this.loadScript('https://pay.google.com/gp/p/js/pay.js')
            return new google.payments.api.PaymentsClient({
                environment:
                    process.env.NODE_ENV === 'production'
                        ? 'PRODUCTION'
                        : 'TEST'
            })
        } else if (selectedMethod === 'web_apple_pay') {
            await this.loadScript(
                'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js'
            )
            return null // Apple Pay 使用 session-based 初始化
        }
    },

    /**
     * 检查支付方式可用性
     * @param {Object} params - 检查参数
     * @returns {Object} { available: Boolean, reason: String }
     */
    async checkAvailability ({ selectedMethod, paymentClient }) {
        if (selectedMethod === 'web_google_pay') {
            if (process.env.NODE_ENV === 'production') {
                return { available: false, reason: 'Google Pay disabled in production' }
            }
            return { available: true, reason: 'Google Pay available in test env' }
        } else if (selectedMethod === 'web_apple_pay') {
            const available = window.ApplePaySession && ApplePaySession.canMakePayments()
            return {
                available: !!available,
                reason: available ? 'Apple Pay available' : 'ApplePaySession not found or cannot make payments'
            }
        }
        return { available: false, reason: 'Unknown method' }
    },

    /**
     * 处理支付流程
     * @param {Object} params - 支付参数
     */
    async handlePayment ({
        selectedMethod,
        paymentClient,
        billingRule,
        selectedSubscription,
        repaymentInfo,
        deviceCode,
        outletId,
        config,
        createPaymentOrder,
        doSubscriptionPayment,
        doRepay,
        afterPayment,
        toast,
        hideLoading
    }) {
        const hasRepayment =
            !!repaymentInfo &&
            repaymentInfo.have_unpaid &&
            repaymentInfo.currency

        if (selectedMethod === 'web_google_pay') {
            const tokenizationSpecification = {
                type: 'PAYMENT_GATEWAY',
                parameters: {
                    gateway: 'paylink',
                    gatewayMerchantId: config.merchantGatewayID
                }
            }
            const baseCardPaymentMethod = {
                type: 'CARD',
                parameters: {
                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                    allowedCardNetworks: [
                        'AMEX',
                        'DISCOVER',
                        'INTERAC',
                        'JCB',
                        'MASTERCARD',
                        'VISA'
                    ]
                }
            }
            const cardPaymentMethod = {
                ...baseCardPaymentMethod,
                tokenizationSpecification
            }
            const paymentDataRequest = {
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: [cardPaymentMethod],
                transactionInfo: {
                    totalPriceStatus:
                        selectedSubscription?.id || hasRepayment
                            ? 'FINAL'
                            : 'ESTIMATED',
                    totalPrice:
                        Number(
                            selectedSubscription?.price ??
                            (hasRepayment
                                ? repaymentInfo.unpaid_fee
                                : billingRule.pre_auth_amount)
                        ).toFixed(2),
                    currencyCode:
                        selectedSubscription?.currency?.toUpperCase() ||
                        (hasRepayment
                            ? repaymentInfo.currency?.toUpperCase()
                            : billingRule.currency?.toUpperCase()),
                    countryCode: 'US'
                },
                merchantInfo: {
                    merchantName: config.merchantName,
                    merchantId: config.merchantID,
                    authJwt: config.authJwt
                }
            }

            try {
                const paymentData = await paymentClient.loadPaymentData(
                    paymentDataRequest
                )
                const paymentToken =
                    paymentData.paymentMethodData.tokenizationData.token
                await this.processPayment({
                    paymentToken,
                    selectedSubscription,
                    repaymentInfo,
                    deviceCode,
                    outletId,
                    config,
                    selectedMethod,
                    createPaymentOrder,
                    doSubscriptionPayment,
                    doRepay,
                    afterPayment
                })
            } catch (err) {
                toast('Google Payment error: ' + err, { persistent: true })
                hideLoading()
            }
        } else if (selectedMethod === 'web_apple_pay') {
            const applePaymentRequest = hasRepayment
                ? {
                      countryCode: 'US',
                      currencyCode: repaymentInfo.currency.toUpperCase(),
                      supportedNetworks: [
                          'visa',
                          'masterCard',
                          'amex',
                          'discover'
                      ],
                      merchantCapabilities: [
                          'supports3DS',
                          'supportsCredit',
                          'supportsDebit'
                      ],
                      total: {
                          label: config.merchantName,
                          amount: repaymentInfo.unpaid_fee
                      }
                  }
                : {
                      countryCode: 'US',
                      currencyCode:
                          selectedSubscription?.currency?.toUpperCase() ||
                          billingRule?.currency?.toUpperCase(),
                      supportedNetworks: [
                          'visa',
                          'masterCard',
                          'amex',
                          'discover'
                      ],
                      merchantCapabilities: [
                          'supports3DS',
                          'supportsCredit',
                          'supportsDebit'
                      ],
                      total: {
                          label:
                              selectedSubscription?.price != null
                                  ? config.merchantName
                                  : 'pre-auth',
                          amount: Number.isFinite(
                              Number(selectedSubscription?.price)
                          )
                              ? Number(selectedSubscription.price).toFixed(2)
                              : Number(
                                    billingRule?.pre_auth_amount || 0
                                ).toFixed(2)
                      }
                  }

            const appleSession = new ApplePaySession(3, applePaymentRequest)

            appleSession.onvalidatemerchant = async event => {
                try {
                    const validation_url = event.validationURL
                    const validateMerchantRes = await applePayValidateMerchant({
                        validation_url,
                        wallet_merchant_id: config.merchantGatewayID,
                        wallet_merchant_name: config.merchantName
                    })
                    const merchantSession = validateMerchantRes.data
                        ?.merchantSession
                        ? JSON.parse(validateMerchantRes.data.merchantSession)
                        : {}
                    appleSession.completeMerchantValidation(merchantSession)
                } catch (err) {
                    console.error('Merchant validation failed:', err)
                    appleSession.abort()
                    hideLoading()
                }
            }

            appleSession.onpaymentauthorized = async event => {
                try {
                    const paymentToken = event.payment.token
                    await this.processPayment({
                        paymentToken,
                        selectedSubscription,
                        repaymentInfo,
                        deviceCode,
                        outletId,
                        config,
                        selectedMethod,
                        createPaymentOrder,
                        doSubscriptionPayment,
                        doRepay,
                        afterPayment
                    })
                    appleSession.completePayment(ApplePaySession.STATUS_SUCCESS)
                } catch (err) {
                    appleSession.completePayment(ApplePaySession.STATUS_FAILURE)
                    toast('Payment failed: ' + err, { persistent: true })
                    hideLoading()
                }
            }

            appleSession.begin()
        }
    },

    /**
     * 处理支付结果
     * @param {Object} params - 支付结果参数
     */
    async processPayment ({
        paymentToken,
        selectedSubscription,
        repaymentInfo,
        deviceCode,
        outletId,
        config,
        selectedMethod,
        createPaymentOrder,
        doSubscriptionPayment,
        doRepay,
        afterPayment
    }) {
        const gatewayId = config?.fortisGatewayId || 0
        let internalId, rentPaymentId, subscriptionPaymentId
        if (selectedSubscription?.id) {
            const doSubscriptionPaymentRes = await doSubscriptionPayment({
                subscriptionId: selectedSubscription.id,
                paymentMethod: selectedMethod,
                gatewayId,
                outletsId: outletId,
                autoRenew: Number(selectedSubscription.auto_renew)
            })
            internalId = getClientNextActionField(
                doSubscriptionPaymentRes,
                'internal_id'
            )
            subscriptionPaymentId =
                doSubscriptionPaymentRes.data?.subscription_payment_id
        } else if (repaymentInfo) {
            const doRepayRes = await doRepay({
                paymentMethod: selectedMethod,
                gatewayId
            })
            internalId = getClientNextActionField(doRepayRes, 'internal_id')
            rentPaymentId = doRepayRes.data?.rent_payment_id
        } else {
            const createPaymentOrderRes = await createPaymentOrder(
                selectedMethod,
                gatewayId,
                deviceCode
            )
            internalId = getClientNextActionField(
                createPaymentOrderRes,
                'internal_id'
            )
            rentPaymentId = createPaymentOrderRes.data?.rent_payment_id
        }

        afterPayment({
            paymentToken,
            internalId,
            rentPaymentId,
            subscriptionPaymentId
        })
    },

    /**
     * 加载外部脚本
     * @param {String} src - 脚本地址
     */
    async loadScript (src) {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = src
            script.async = true
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
        })
    }
}
